import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fire, MagnifyingGlass, CaretDown, CaretUp } from "@phosphor-icons/react";
import { timingHauls, timingHaulsDetail } from "../../lib/timing-api";
import { POSITIONS } from "../../lib/timing-i18n";
import { track } from "../../lib/analytics";

// HAUL = 10+ points in a single GW · MEGA = 15+. Historical data 2016-2026.

const SORTS = [
    ["hauls", "Počet haulů"],
    ["rate", "Haul %"],
    ["mega", "Mega (15+)"],
    ["max_pts", "Max bodů"],
];

function OpponentChip({ o }) {
    return (
        <span className="font-mono text-[9px] border border-fa-yellow/25 bg-fa-yellow/[0.06] text-fa-yellow px-1.5 py-0.5 whitespace-nowrap"
              title={`${o.hauls}× haul z ${o.games} zápasů proti ${o.opponent}`}>
            {o.opponent} {o.hauls}×
        </span>
    );
}

function DetailRow({ playerKey }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        track("hauls_detail", { tool: "timing", meta: { player_key: playerKey } });
        timingHaulsDetail(playerKey).then(setDetail).catch(() => {});
    }, [playerKey]);
    if (!detail) return (
        <div className="p-3 font-mono text-[10px] text-white/40">Načítám detail…</div>
    );
    const withHauls = detail.opponents.filter((o) => o.hauls > 0);
    const seasons = Object.entries(detail.by_season || {});
    return (
        <div className="p-3 space-y-3" data-testid={`hauls-detail-${playerKey}`}>
            {seasons.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/45">Hauly po sezónách:</span>
                    {seasons.map(([s, n]) => (
                        <span key={s} className="font-mono text-[9px] border border-white/15 px-1.5 py-0.5">
                            {s}: <strong className="text-fa-yellow">{n}</strong>
                        </span>
                    ))}
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
                            <th className="px-2 py-1">Soupeř</th>
                            <th className="px-2 py-1 text-right">Zápasů proti</th>
                            <th className="px-2 py-1 text-right">Haulů</th>
                            <th className="px-2 py-1 text-right">Mega (15+)</th>
                            <th className="px-2 py-1 text-right">Haul %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {withHauls.map((o) => (
                            <tr key={o.opponent} className="border-b border-white/[0.04]">
                                <td className="px-2 py-1 font-display font-bold text-xs">{o.opponent}</td>
                                <td className="px-2 py-1 text-right font-mono text-[10px] text-white/60 tabular-nums">{o.games}</td>
                                <td className="px-2 py-1 text-right font-mono text-[11px] text-fa-yellow font-bold tabular-nums">{o.hauls}</td>
                                <td className="px-2 py-1 text-right font-mono text-[10px] text-orange-300 tabular-nums">{o.mega || "—"}</td>
                                <td className="px-2 py-1 text-right font-mono text-[10px] tabular-nums">{o.haul_rate}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {withHauls.length === 0 && (
                    <div className="font-mono text-[10px] text-white/40 p-2">Žádné hauly.</div>
                )}
            </div>
        </div>
    );
}

export default function HaulsBoard() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [position, setPosition] = useState("");
    const [team, setTeam] = useState("");
    const [minGames, setMinGames] = useState(50);
    const [sort, setSort] = useState("hauls");
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        track("hauls_view", { tool: "timing" });
    }, []);

    const load = async () => {
        setBusy(true);
        try {
            setData(await timingHauls({ q, position, team, minGames, sort }));
        } catch { /* ignore */ } finally { setBusy(false); }
    };

    useEffect(() => {
        const tm = setTimeout(load, q ? 350 : 0);
        return () => clearTimeout(tm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, position, team, minGames, sort]);

    const rows = data?.rows || [];

    return (
        <div className="space-y-4" data-testid="hauls-board">
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <h2 className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
                    <Fire size={16} weight="fill" className="text-fa-yellow" />
                    Hauly — historická exploze bodů
                </h2>
                <p className="font-mono text-[10px] text-white/50 mt-1 max-w-3xl leading-relaxed">
                    HAUL = 10+ bodů v jednom GW · MEGA = 15+. Data 2016/17–2025/26 (odehrané zápasy, min. 1 minuta).
                    Klikni na hráče a uvidíš, proti kterým soupeřům hauly dělá a jak často.
                </p>
            </header>

            <div className="flex items-center gap-2 flex-wrap" data-testid="hauls-filters">
                <div className="relative">
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Hledat hráče…"
                        data-testid="hauls-search"
                        className="bg-[#0d0d0d] border border-white/15 pl-7 pr-2 py-1.5 font-mono text-[11px] w-48 focus:outline-none focus:border-fa-yellow"
                    />
                </div>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setPosition("")}
                            className={`font-mono text-[9px] uppercase px-2 py-1.5 border ${!position ? "bg-fa-yellow text-black border-fa-yellow font-bold" : "border-white/15 text-white/60"}`}>
                        Vše
                    </button>
                    {POSITIONS.map((p) => (
                        <button key={p} type="button" onClick={() => setPosition(position === p ? "" : p)}
                                data-testid={`hauls-pos-${p}`}
                                className={`font-mono text-[9px] uppercase px-2 py-1.5 border ${position === p ? "bg-fa-yellow text-black border-fa-yellow font-bold" : "border-white/15 text-white/60"}`}>
                            {p}
                        </button>
                    ))}
                </div>
                <select value={team} onChange={(e) => setTeam(e.target.value)} data-testid="hauls-team-filter"
                        className="bg-[#0d0d0d] border border-white/15 px-2 py-1.5 font-mono text-[10px] text-white/70 focus:outline-none max-w-[180px]">
                    <option value="">Všechny týmy</option>
                    {(data?.teams || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={minGames} onChange={(e) => setMinGames(Number(e.target.value))} data-testid="hauls-min-games"
                        className="bg-[#0d0d0d] border border-white/15 px-2 py-1.5 font-mono text-[10px] text-white/70 focus:outline-none">
                    {[10, 30, 50, 100, 150].map((n) => <option key={n} value={n}>min {n} zápasů</option>)}
                </select>
                <div className="flex items-center gap-1 ml-auto">
                    {SORTS.map(([k, label]) => (
                        <button key={k} type="button" onClick={() => setSort(k)} data-testid={`hauls-sort-${k}`}
                                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1.5 border ${sort === k ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                <table className="w-full text-left" data-testid="hauls-table">
                    <thead>
                        <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                            <th className="px-3 py-2">#</th>
                            <th className="px-3 py-2">Hráč</th>
                            <th className="px-3 py-2 text-right">Zápasy</th>
                            <th className="px-3 py-2 text-right">Hauly</th>
                            <th className="px-3 py-2 text-right">Haul %</th>
                            <th className="px-3 py-2 text-right">Mega 15+</th>
                            <th className="px-3 py-2 text-right">Max b.</th>
                            <th className="px-3 py-2">Nejčastější oběti</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <React.Fragment key={r.player_key}>
                                <tr className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                                    data-testid={`hauls-row-${r.player_key}`}
                                    onClick={() => setExpanded(expanded === r.player_key ? null : r.player_key)}>
                                    <td className="px-3 py-2 font-mono text-[10px] text-white/40 tabular-nums">{i + 1}</td>
                                    <td className="px-3 py-2">
                                        <Link to={`/players/${encodeURIComponent(r.player_key)}`}
                                              onClick={(e) => e.stopPropagation()}
                                              data-testid={`hauls-profile-${r.player_key}`}
                                              className="font-display font-bold text-sm hover:text-fa-mint transition-colors">{r.display_name}</Link>
                                        <span className="font-mono text-[8px] uppercase text-white/40 ml-2">
                                            {(r.positions || []).join("/")}{r.team ? ` · ${r.team}` : ""} · {r.seasons_count}× sez.
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-[10px] text-white/60 tabular-nums">{r.games}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[12px] text-fa-yellow font-bold tabular-nums">{r.hauls}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">{r.haul_rate}%</td>
                                    <td className="px-3 py-2 text-right font-mono text-[10px] text-orange-300 tabular-nums">{r.mega}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[10px] text-white/60 tabular-nums">{r.max_pts}</td>
                                    <td className="px-3 py-2">
                                        <span className="flex items-center gap-1 flex-wrap">
                                            {(r.top_opponents || []).map((o) => <OpponentChip key={o.opponent} o={o} />)}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-fa-sky">
                                        {expanded === r.player_key ? <CaretUp size={12} /> : <CaretDown size={12} />}
                                    </td>
                                </tr>
                                {expanded === r.player_key && (
                                    <tr className="border-b border-white/5 bg-black/40">
                                        <td colSpan={9}><DetailRow playerKey={r.player_key} /></td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {busy && <div className="p-4 font-mono text-[10px] text-white/40">Načítám…</div>}
                {!busy && rows.length === 0 && (
                    <div className="p-6 font-mono text-[11px] text-white/50 text-center" data-testid="hauls-empty">
                        Žádní hráči neodpovídají filtrům.
                    </div>
                )}
            </div>
            {data && (
                <p className="font-mono text-[9px] text-white/35">
                    Zobrazeno {rows.length} z {data.total_matching} hráčů (min. {minGames} zápasů, alespoň 1 haul).
                </p>
            )}
        </div>
    );
}
